import React, { useEffect } from "react";
import { useTenant } from "../contexts/TenantContext";
import { isAnalyticsConfigured } from "../services/analyticsConfig";

/**
 * Injects the tenant's GTM / GA / Google Ads scripts into the document.
 *
 * The analytics config comes from the resolved tenant (`useTenant()` — the §1
 * Spanner-backed tenant-config bundle), not a direct Firestore read. The tenant
 * (and its `analytics` block) is resolved once, by the tenant provider, from the
 * hostname; re-resolving `tenants` / `domain-mappings` here would duplicate that.
 */
const GTMInjector: React.FC = () => {
  const { config } = useTenant();
  const tenantId = config?.id;

  useEffect(() => {
    // Add CSP violation listener
    const handleCSPViolation = (event: SecurityPolicyViolationEvent) => {};
    document.addEventListener("securitypolicyviolation", handleCSPViolation);

    const injectGTM = () => {
      // Wait for the tenant to resolve before touching analytics.
      if (!config || !tenantId) {
        return;
      }

      // Analytics scripts from the resolved tenant config. Only inject values
      // that are actually configured — placeholder/empty ids (e.g. the
      // GTM-XXXXXXX that default-tenant-config.json ships) are skipped so GTM/GA
      // don't request bogus ids and log errors on every reload. Genuinely
      // malformed real ids are still injected so their failure stays visible.
      const analytics = config.analytics;
      const configured = (v?: string | null): string =>
        isAnalyticsConfigured(v) ? v : "";
      const gtmHead = configured(analytics?.gtmHead);
      const gtmBody = configured(analytics?.gtmBody);
      const googleTag = configured(analytics?.googleTag);
      const gaHead = configured(analytics?.gaHead);

      // Skip if analytics are already initialized for this tenant.
      const currentTenant = document.documentElement.getAttribute(
        "data-analytics-tenant"
      );
      if (currentTenant === tenantId) {
        return;
      }

      // Remove any existing analytics scripts and dataLayer
      const cleanup = () => {
        document
          .querySelectorAll("[data-analytics]")
          .forEach((el) => el.remove());
        // Reset dataLayer and gtag
        delete (window as any).dataLayer;
        delete (window as any).gtag;
        // Initialize fresh dataLayer
        (window as any).dataLayer = [];
        (window as any).gtag = function () {
          (window as any).dataLayer.push(arguments);
        };
      };

      // Helper function to inject scripts
      const injectScripts = (html: string, type: string) => {
        if (!html) {
          return;
        }

        // Check if this type of script is already injected
        if (document.querySelector(`[data-analytics="${type}"]`)) {
          return;
        }

        // For Google Ads tags, prevent duplicate AW- prefix
        if (type === "google-tag" && html.includes("AW-")) {
          const existingGoogleAdsScript = document.querySelector(
            'script[src*="googleads.g.doubleclick.net"]'
          );
          if (existingGoogleAdsScript) {
            return;
          }
          // Remove duplicate AW- prefix if present
          html = html.replace("AW-AW-", "AW-");
        }

        // Create temporary div to parse HTML
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        // Process scripts
        tempDiv.querySelectorAll("script").forEach((script) => {
          const scriptSrc = script.getAttribute("src");
          const scriptContent = script.textContent;

          // Skip if script already exists
          const scriptExists = Array.from(document.scripts).some((s) => {
            if (scriptSrc && s.src === scriptSrc) return true;
            if (scriptContent && s.textContent === scriptContent) return true;
            return false;
          });

          if (scriptExists) {
            return;
          }

          // Create new script element
          const newScript = document.createElement("script");
          newScript.setAttribute("data-analytics", type);

          // Copy attributes
          Array.from(script.attributes).forEach((attr) => {
            if (attr.name !== "data-analytics") {
              newScript.setAttribute(attr.name, attr.value);
            }
          });

          // Handle src vs inline scripts
          if (scriptSrc) {
            newScript.src = scriptSrc;
            newScript.async = true;
          } else if (scriptContent) {
            newScript.textContent = scriptContent;
          }

          // Add to document with error handling
          try {
            document.head.appendChild(newScript);
          } catch (error) {}
        });

        // Process noscript tags
        tempDiv.querySelectorAll("noscript").forEach((noscript) => {
          const noscriptContent = noscript.innerHTML;

          // Skip if noscript already exists
          const noscriptExists = Array.from(
            document.getElementsByTagName("noscript")
          ).some((n) => n.innerHTML === noscriptContent);

          if (noscriptExists) {
            return;
          }

          const newNoscript = document.createElement("noscript");
          newNoscript.setAttribute("data-analytics", type);
          newNoscript.innerHTML = noscriptContent;
          document.body.insertBefore(newNoscript, document.body.firstChild);
        });
      };

      // Clean up existing scripts
      cleanup();

      // Mark document with current tenant
      document.documentElement.setAttribute("data-analytics-tenant", tenantId);

      // Inject GTM first
      injectScripts(gtmHead, "gtm-head");
      injectScripts(gtmBody, "gtm-body");

      // Wait a short time for GTM to initialize and potentially inject Google Ads
      window.setTimeout(() => {
        // Only inject Google Ads tag if not already present through GTM
        const existingGoogleAdsScript = document.querySelector(
          'script[src*="googleads.g.doubleclick.net"]'
        );
        if (!existingGoogleAdsScript && googleTag) {
          injectScripts(googleTag, "google-tag");
        }

        injectScripts(gaHead, "ga-head");
      }, 1000);
    };

    injectGTM();

    // Cleanup function
    return () => {
      document.removeEventListener(
        "securitypolicyviolation",
        handleCSPViolation
      );
      if (document.documentElement.getAttribute("data-analytics-tenant")) {
        document
          .querySelectorAll("[data-analytics]")
          .forEach((el) => el.remove());
        document.documentElement.removeAttribute("data-analytics-tenant");
        delete (window as any).dataLayer;
        delete (window as any).gtag;
      }
    };
  }, [config, tenantId]);

  return null; // This component only injects scripts
};

export default GTMInjector;
